import { adapter } from '@seek/tealium-adapter';

import {
  type DataLayer,
  LinkPosition,
  type TrackFn,
  trackFunction,
  configureAdapter,
} from '.';

import type {
  HeaderFooterEventName,
  HeaderFooterAnalytics,
  HeaderFooterLinkClickedProps,
  HeaderFooterNavigationPressedProps,
} from './headerFooter';

export type EventName =
  | 'recommended_jobs_displayed'
  | 'job_save_succeeded'
  | 'job_unsave_succeeded'
  | 'profile_pressed'
  | 'job_details_displayed'
  | 'job_details_title_pressed'
  | 'job_share_pressed'
  | 'report_job_pressed'
  | 'jobs_by_company_pressed'
  | 'company_profile_pressed'
  | 'company_reviews_pressed'
  | 'job_apply_pressed'
  | 'lmis_viewed'
  | 'lmis_pressed'
  | 'job_contact_pressed'
  | 'opportunity_viewed'
  | 'opportunity_pressed'
  | 'top_applicant_badge_pressed'
  | HeaderFooterEventName;

export type RecommendedJobsDataLayer = DataLayer &
  JobDetailsDataLayer & {
    sessionId?: string;
    jobsCount?: string;
    jobId?: string;
    experiments?: unknown[];
    opportunityType?: 'refresh' | 'recruiter';
  };

export type JobDetailsDataLayer = {
  linkPosition?: LinkPosition;
  // share
  jobShareType?: 'email' | 'linkedin' | 'facebook' | 'twitter';
  // lmis
  lmisSnippet?: Record<string, string>;
  // jdv
  seekAdvertiserId?: string;
  advertiserName?: string;
  companyId?: string;
  companyName?: string;
  companyRating?: string;
  isPrivateAdvertiser?: boolean;
  isStrongApplicantBadgeShown?: boolean;
  jobPostedTime?: string;
  jobId?: string;
  jobContacts?: string[];
  jobIsLinkOut?: boolean;
  jobHasRoleRequirements?: boolean;
  jobHasScreen?: boolean;
  jobPostingType?: string;
  jobListingType?: string | null;
  jobClassification?: string | null;
  jobSubClassification?: string | null;
  jobTitle?: string;
  jobSalary?: string;
  jobSalaryType?: string;
  jobSalaryCurrency?: string;
  jobArea?: string | null;
  jobLocation?: string | null;
  jobWhereId?: string;
  jobClassificationId?: string | null;
  jobSubClassificationId?: string | null;
  jobStatus?: string;
  jobViewType?: string;
  jobListingPosition?: string;
  jobContactType?: string;
  jobContactAction?: 'click' | 'copy';
  jobWorkType?: string;
  searchKeyword?: string;
};

export interface RecommendedJobsAnalytics extends HeaderFooterAnalytics {
  recommendedJobsPageLoaded: (props: RecommendedJobsDataLayer) => void;
  profileViewed: () => void;
  jobSaved: (props: {
    jobId: JobDetailsDataLayer['jobId'];
    linkPosition?: JobDetailsDataLayer['linkPosition'];
  }) => void;
  jobUnsaved: (props: {
    jobId: JobDetailsDataLayer['jobId'];
    linkPosition?: JobDetailsDataLayer['linkPosition'];
  }) => void;
  jobDetailsViewed: (props: JobDetailsDataLayer) => void;
  jobDetailsTitleClicked: () => void;
  jobShared: (props: {
    jobShareType: JobDetailsDataLayer['jobShareType'];
  }) => void;
  jobApplied: () => void;
  jobReported: () => void;
  jobByThisCompanyClicked: () => void;
  companyProfileClicked: () => void;
  companyReviewsClicked: () => void;
  learningSnippetViewed: (props: {
    lmisSnippet?: JobDetailsDataLayer['lmisSnippet'];
  }) => void;
  learningSnippetClicked: (props: {
    lmisSnippet?: JobDetailsDataLayer['lmisSnippet'];
  }) => void;
  contactHirerInteracted: (props: {
    jobContactType: JobDetailsDataLayer['jobContactType'];
    jobContactAction: JobDetailsDataLayer['jobContactAction'];
  }) => void;
  refreshNewRecsViewed: (jobsCount: string) => void;
  refreshNewRecsClicked: (jobsCount: string) => void;
  topApplicantBadgeClicked: () => void;
  sourcrImpression: () => void;
  sourcrClicked: () => void;
}

const defaultInitialDataLayerRecommendedJobs = {
  currentPage: 'recommended jobs',
  experimentId: undefined,
};

const createRecommendedJobsAnalytics = (
  initialValues?: any,
): RecommendedJobsAnalytics => {
  const tealium = adapter({});
  configureAdapter(tealium);

  let dataLayer: RecommendedJobsDataLayer = {
    ...defaultInitialDataLayerRecommendedJobs,
    ...initialValues,
  };

  let jobDetailsDataLayer: JobDetailsDataLayer = {};

  const track: TrackFn<EventName, RecommendedJobsDataLayer> = trackFunction;

  const recommendedJobsPageLoaded = ({
    profileGuid,
    loginId,
    jobsCount,
    brand,
    siteCountry,
    currentPage,
  }: RecommendedJobsDataLayer) => {
    dataLayer = {
      ...dataLayer,
      profileGuid,
      loginId,
      brand,
      siteCountry,
      currentPage,
    };

    track('view', 'recommended_jobs_displayed', {
      ...dataLayer,
      jobsCount,
    });
  };

  const profileViewed = () => {
    track('link', 'profile_pressed', {
      ...dataLayer,
      linkName: 'profile_pressed',
      thirdPartyLink: false,
      linkPosition: LinkPosition.Body,
    });
  };

  const jobDetailsViewed = (data: JobDetailsDataLayer) => {
    jobDetailsDataLayer = { ...data };

    track('link', 'job_details_displayed', {
      ...dataLayer,
      linkName: 'job_details_displayed',
      ...jobDetailsDataLayer,
    });
  };

  const jobDetailsTitleClicked = () => {
    track('link', 'job_details_title_pressed', {
      ...dataLayer,
      linkName: 'job_details_title_pressed',
      ...jobDetailsDataLayer,
    });
  };

  const jobSaved = ({
    jobId,
    linkPosition = LinkPosition.Listing,
  }: {
    jobId: JobDetailsDataLayer['jobId'];
    linkPosition?: JobDetailsDataLayer['linkPosition'];
  }) => {
    track('link', 'job_save_succeeded', {
      ...dataLayer,
      linkName: 'job_save_succeeded',
      jobId,
      linkPosition,
      ...(linkPosition === 'job details' && jobDetailsDataLayer),
    });
  };

  const jobUnsaved = ({
    jobId,
    linkPosition = LinkPosition.Listing,
  }: {
    jobId: JobDetailsDataLayer['jobId'];
    linkPosition?: JobDetailsDataLayer['linkPosition'];
  }) => {
    track('link', 'job_unsave_succeeded', {
      ...dataLayer,
      linkName: 'job_unsave_succeeded',
      jobId,
      linkPosition,
      ...(linkPosition === 'job details' && jobDetailsDataLayer),
    });
  };

  const jobShared = ({
    jobShareType,
  }: {
    jobShareType: JobDetailsDataLayer['jobShareType'];
  }) => {
    track('link', 'job_share_pressed', {
      ...dataLayer,
      linkName: 'job_share_pressed',
      jobShareType,
      ...jobDetailsDataLayer,
    });
  };

  const jobApplied = () => {
    track('link', 'job_apply_pressed', {
      ...dataLayer,
      ...jobDetailsDataLayer,
      linkName: 'job_apply_pressed',
    });
  };

  const jobReported = () => {
    track('link', 'report_job_pressed', {
      ...dataLayer,
      linkName: 'report_job_pressed',
      ...jobDetailsDataLayer,
    });
  };

  const jobByThisCompanyClicked = () => {
    track('link', 'jobs_by_company_pressed', {
      ...dataLayer,
      linkName: 'jobs_by_company_pressed',
      ...jobDetailsDataLayer,
    });
  };

  const companyProfileClicked = () => {
    track('link', 'company_profile_pressed', {
      ...dataLayer,
      linkName: 'company_profile_pressed',
      ...jobDetailsDataLayer,
    });
  };

  const companyReviewsClicked = () => {
    track('link', 'company_reviews_pressed', {
      ...dataLayer,
      linkName: 'company_reviews_pressed',
      ...jobDetailsDataLayer,
    });
  };

  const learningSnippetViewed = ({ lmisSnippet }: { lmisSnippet?: any }) => {
    track('link', 'lmis_viewed', {
      ...dataLayer,
      linkName: 'lmis_viewed',
      ...(lmisSnippet && { lmisSnippet }),
      ...jobDetailsDataLayer,
    });
  };

  const learningSnippetClicked = ({ lmisSnippet }: { lmisSnippet?: any }) => {
    track('link', 'lmis_pressed', {
      ...dataLayer,
      linkName: 'lmis_pressed',
      ...(lmisSnippet && { lmisSnippet }),
      ...jobDetailsDataLayer,
    });
  };

  const contactHirerInteracted = ({
    jobContactType,
    jobContactAction,
  }: {
    jobContactType: JobDetailsDataLayer['jobContactType'];
    jobContactAction: JobDetailsDataLayer['jobContactAction'];
  }) => {
    track('link', 'job_contact_pressed', {
      ...dataLayer,
      ...jobDetailsDataLayer,
      linkName: 'job_contact_pressed',
      jobContactType,
      jobContactAction,
    });
  };

  const refreshNewRecsViewed = (jobsCount: string) => {
    track('link', 'opportunity_viewed', {
      ...dataLayer,
      linkName: 'opportunity_viewed',
      opportunityType: 'refresh',
      thirdPartyLink: false,
      jobsCount,
    });
  };

  const refreshNewRecsClicked = (jobsCount: string) => {
    track('link', 'opportunity_pressed', {
      ...dataLayer,
      linkName: 'opportunity_pressed',
      opportunityType: 'refresh',
      thirdPartyLink: false,
      jobsCount,
    });
  };

  const topApplicantBadgeClicked = () => {
    track('link', 'top_applicant_badge_pressed', {
      ...dataLayer,
      linkName: 'top_applicant_badge_pressed',
      ...jobDetailsDataLayer,
    });
  };

  const sourcrImpression = () => {
    track('link', 'opportunity_viewed', {
      ...dataLayer,
      linkName: 'opportunity_viewed',
      opportunityType: 'recruiter',
    });
  };

  const sourcrClicked = () => {
    track('link', 'opportunity_pressed', {
      ...dataLayer,
      linkName: 'opportunity_pressed',
      opportunityType: 'recruiter',
    });
  };

  const headerFooterLinkClicked = (props: HeaderFooterLinkClickedProps) => {
    track('link', 'navigation_pressed', {
      ...dataLayer,
      ...props,
    });
  };

  const headerFooterNavigationPressed = (
    props: HeaderFooterNavigationPressedProps,
  ) => {
    track('link', 'navigation_pressed', {
      ...dataLayer,
      ...props,
    });
  };
  return {
    headerFooterLinkClicked,
    headerFooterNavigationPressed,
    recommendedJobsPageLoaded,
    profileViewed,
    jobSaved,
    jobUnsaved,
    jobDetailsViewed,
    jobDetailsTitleClicked,
    jobShared,
    jobApplied,
    jobReported,
    jobByThisCompanyClicked,
    companyProfileClicked,
    companyReviewsClicked,
    learningSnippetViewed,
    learningSnippetClicked,
    contactHirerInteracted,
    refreshNewRecsViewed,
    refreshNewRecsClicked,
    topApplicantBadgeClicked,
    sourcrImpression,
    sourcrClicked,
  };
};

export default createRecommendedJobsAnalytics;
